/**
 * useNativeSipCall - React hook that drives the native libwebrtc + SIP engine
 * on iOS/Android via the ElefaSip Capacitor plugin.
 *
 * Mirrors the public surface of `useFreeSwitchCall` so `useActiveCallProvider`
 * can swap implementations behind `useIsNative()` without React consumers
 * caring which engine is in use.
 *
 * Backend contract is identical to web SIP.js:
 *   1. POST /freeswitch-connect-start  → returns sipServer WSS + creds + accountcode
 *   2. Native SIP over WSS 7443        → Kamailio SBC (adowaitel002)
 *   3. INVITE with X-User-Id / X-Call-Id / X-Caller-Id / X-Account-Code
 *      → CDR pipeline + ASTPP billing work unchanged
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { callFailureLine, describeCallFailure, type CallFailureCopy } from '@/lib/callFailureCopy';
import {
  ElefaSipPlugin,
  isNativeSipAvailable,
  type SipCallEvent,
} from '@/lib/elefa-sip-plugin';
import { notifyCallEnd } from '@/lib/call-billing';
import { toast } from 'sonner';

/**
 * Mask raw SIP / transport errors. The user must only ever see
 * credit-related messages (per product rule); everything else is
 * collapsed into a generic retry hint so 403 / 408 / "registration
 * failed" infrastructure noise never reaches the UI.
 */
/**
 * Customer-facing wording for a failed call comes from the shared map in
 * src/lib/callFailureCopy.ts (one table, same words on web/iOS/Android).
 * The raw cause stays in the console and in the sheet's Details line.
 */
async function parseConnectStartFailure(
  data: ConnectStartResponse | null | undefined,
  invokeError: unknown,
): Promise<Partial<ConnectStartResponse>> {
  if (data?.message || data?.error) return data;

  const context = (invokeError as { context?: unknown } | null)?.context;
  if (context instanceof Response) {
    try {
      const contentType = context.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        return await context.clone().json();
      }
      const text = await context.clone().text();
      if (text.trim()) return { message: text };
    } catch {
      // Fall through to the SDK error message below.
    }
  }

  return {
    message: invokeError instanceof Error ? invokeError.message : data?.error || 'connect-start failed',
    error: data?.error,
  };
}


import type { SharedCallStatus } from '@/lib/call-status';

export type NativeCallStatus = SharedCallStatus;

interface ConnectStartResponse {
  success: boolean;
  callId: string;
  sessionId: string;
  sipUsername: string;
  sipPassword: string;
  sipServer: string;        // wss://host:7443[/path]
  sipServerTls: string;     // legacy fallback only; raw 5061 is not used by iOS
  dialDestination: string;
  callerId: string;
  astppAccountNumber?: string;
  ratePerMinuteCents?: number;
  reservedCents?: number;
  availableBalanceCents?: number;
  minimumRequiredCents?: number;
  maxSeconds?: number | null;
  maxCallSeconds?: number | null;
  lowBalanceWarningSeconds?: number | null;
  message?: string;
  destination: { number: string; country: string; countryCode: string };
  error?: string;
}

interface NativeCallData {
  callId: string;
  sessionId: string;
  destination: { number: string; country: string; countryCode: string };
  ratePerMinuteCents: number;
  reservedCents: number;
  callerId?: string;
  astppAccountNumber?: string;
  availableBalanceCents?: number;
  minimumRequiredCents?: number;
  maxCallSeconds?: number | null;
  lowBalanceWarningSeconds?: number | null;
}

type NativeIceServer = {
  urls: string | string[];
  username?: string;
  credential?: string;
};

async function loadIceServers(): Promise<NativeIceServer[]> {
  const fallback: NativeIceServer[] = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ];
  try {
    const { data, error } = await supabase.functions.invoke<{ iceServers?: NativeIceServer[] }>('elefa-turn-credentials');
    if (!error && Array.isArray(data?.iceServers) && data.iceServers.length > 0) return data.iceServers;
  } catch (e) {
    console.warn('[NativeSip] TURN credential fetch failed; using STUN fallback', e);
  }
  return fallback;
}

function isNativeSipRuntimeEnabled(): boolean {
  if (!Capacitor.isNativePlatform() || !isNativeSipAvailable()) return false;
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get('nativeSip');
    if (q === '1') {
      localStorage.setItem('elefa.native_sip', '1');
      return true;
    }
    if (q === '0') {
      localStorage.setItem('elefa.native_sip', '0');
      return false;
    }
    return localStorage.getItem('elefa.native_sip') === '1';
  } catch {
    return false;
  }
}

export function useNativeSipCall() {
  const [callStatus, setCallStatus] = useState<NativeCallStatus>('idle');
  const [callId, setCallId] = useState<string | null>(null);
  const [callData, setCallData] = useState<NativeCallData | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Structured, customer-facing description of the last failure (shared map).
  const [failure, setFailure] = useState<CallFailureCopy | null>(null);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  // Network-drop awareness (W3). When the OS reports offline mid-call we
  // surface a non-fatal degraded flag so the UI can show a "Reconnecting…"
  // chip and the call-quality monitor can downgrade MOS. Native SIP keepalive
  // will time the dialog out on its own; this flag just removes the lag where
  // the in-app UI still shows "connected" for ~30s after Wi-Fi drops.
  const [isNetworkDegraded, setIsNetworkDegraded] = useState(false);

  const durationTimerRef = useRef<number | null>(null);
  const durationRef = useRef(0);
  const listenerRef = useRef<{ remove: () => Promise<void> } | null>(null);
  const activeCallIdRef = useRef<string | null>(null);
  const backendCallIdRef = useRef<string | null>(null);
  const backendSessionIdRef = useRef<string | null>(null);
  const didConnectRef = useRef(false);
  const endNotifiedRef = useRef(false);
  const registeredSipIdentityRef = useRef<string | null>(null);
  const registerPromiseRef = useRef<Promise<void> | null>(null);
  // Re-entry guard: blocks a second initiateCall() while one is already
  // in-flight or active. A duplicate CXStartCallAction triggers CallKit
  // "requesttransaction error 7" which tears down the first call via BYE.
  const inFlightRef = useRef<boolean>(false);
  // Pre-ring cancel guard. Synchronously set by endCall() inside the user's
  // tap handler so that any still-pending await in initiateCall (edge fn,
  // register, INVITE) can short-circuit. Required because activeCallIdRef
  // is only populated AFTER ElefaSipPlugin.call() resolves — tapping End
  // before that point used to flip UI to "ended" while the native INVITE
  // kept ringing the callee with no controls.
  const cancelRequestedRef = useRef<boolean>(false);


  const stopDurationTimer = useCallback(() => {
    if (durationTimerRef.current !== null) {
      window.clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  const startDurationTimer = useCallback(() => {
    stopDurationTimer();
    const startedAt = Date.now();
    durationTimerRef.current = window.setInterval(() => {
      setDuration(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000) as unknown as number;
  }, [stopDurationTimer]);

  // Subscribe to native SIP events once
  useEffect(() => {
    if (!isNativeSipRuntimeEnabled()) return;

    // A2 (2026-06-07): Android App Standby kills the WSS socket silently after
    // ~2 min idle, but the JS layer still has registeredSipIdentityRef set, so
    // the next call setup skips REGISTER and the INVITE goes out on a dead
    // socket → "registration failed" / call fails until app restart.
    // Refs:
    //   - Android Doze/Standby: https://developer.android.com/training/monitoring-device-state/doze-standby
    //   - iOS persistent-registration memory: mem://telephony/persistent-registration-ios
    // Fix: when the app resumes after >60s hidden, invalidate the JS-side
    // registration cache so the very next call forces a fresh idempotent
    // ElefaSipPlugin.register(), which rebuilds the WSS transport if needed.
    // Conservative: only acts after >60s hidden, never during an active call,
    // does NOT touch the native side, does NOT introduce a background ping.
    let pausedAt: number | null = null;
    const STALE_AFTER_MS = 60_000;
    let lifecycleHandle: { remove: () => void } | null = null;
    let cancelledLifecycle = false;
    if (Capacitor.isNativePlatform()) {
      void import('@capacitor/app')
        .then(async ({ App }) => {
          lifecycleHandle = await App.addListener('appStateChange', (state: { isActive: boolean }) => {
            if (state.isActive) {
              const hiddenFor = pausedAt != null ? Date.now() - pausedAt : 0;
              pausedAt = null;
              if (hiddenFor >= STALE_AFTER_MS && !activeCallIdRef.current && !inFlightRef.current) {
                console.info(`[NativeSip] resumed after ${Math.round(hiddenFor / 1000)}s - invalidating register cache for next call`);
                registeredSipIdentityRef.current = null;
              }
            } else {
              pausedAt = Date.now();
            }
          });
          if (cancelledLifecycle) lifecycleHandle.remove();
        })
        .catch((error) => console.warn('[NativeSip] lifecycle bridge skipped', error));
    }

    // ─── SIP PRE-REGISTRATION (cold-start warmup) ────────────────────
    // Without this, the very first call after app launch has to:
    //   (1) hit /freeswitch-connect-start (balance + rate + ANI),
    //   (2) open the WSS transport to fs.elefa.tel:7443,
    //   (3) authenticate REGISTER,
    //   (4) THEN send INVITE.
    // Steps 2+3 add 2–4s and very often time out on the first attempt
    // (mobile radio cold, TURN warmup, TLS handshake), so the second
    // tap is the one that "works". We pre-register on mount using the
    // stable per-user SIP identity returned by /sip-credentials — zero
    // billing side effects — so step 4 is the only thing left to do
    // when the user presses Call. Idempotent on the native side.
    if (isNativeSipRuntimeEnabled()) {
      void (async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;
          const { data, error } = await supabase.functions.invoke<{
            success: boolean;
            sipServer?: string;
            sipServerTls?: string;
            sipUsername?: string;
            sipPassword?: string;
            displayName?: string;
          }>('sip-credentials');
          if (error || !data?.success || !data.sipUsername || !data.sipPassword) return;
          const sipIdentity = `${data.sipServer}|${data.sipServerTls}|${data.sipUsername}|${data.sipPassword}`;
          if (registeredSipIdentityRef.current === sipIdentity || registerPromiseRef.current) return;
          registerPromiseRef.current = ElefaSipPlugin.register({
            sipServerTls: data.sipServerTls!,
            sipServerWss: data.sipServer!,
            username: data.sipUsername!,
            password: data.sipPassword!,
            displayName: data.displayName,
          })
            .then(() => { registeredSipIdentityRef.current = sipIdentity; })
            .catch((e) => { console.warn('[NativeSip] prewarm register failed (non-fatal)', e); registeredSipIdentityRef.current = null; })
            .finally(() => { registerPromiseRef.current = null; });
        } catch (e) {
          console.warn('[NativeSip] prewarm skipped', e);
        }
      })();
    }


    let cancelled = false;
    (async () => {
      const handle = await ElefaSipPlugin.addListener('sipEvent', (event: SipCallEvent) => {
        if (cancelled) return;
        switch (event.type) {
          case 'progress':
            setCallStatus('ringing');
            break;
          case 'connected':
            setCallStatus('connected');
            didConnectRef.current = true;
            if (backendCallIdRef.current) {
              void supabase.functions.invoke('call-control', {
                body: {
                  action: 'connected',
                  callId: backendCallIdRef.current,
                  sessionId: backendSessionIdRef.current || '',
                },
              });
            }
            startDurationTimer();
            break;
          case 'ended': {
            // Capture before the refs below are cleared: "charged" wording
            // depends on whether media ever connected.
            const connectedAtEnd = didConnectRef.current;
            setCallStatus('ended');
            stopDurationTimer();
            if (backendCallIdRef.current && !endNotifiedRef.current) {
              endNotifiedRef.current = true;
              void notifyCallEnd({
                callId: backendCallIdRef.current,
                sessionId: backendSessionIdRef.current || '',
                action: didConnectRef.current ? 'ended' : 'cancelled',
                durationSeconds: durationRef.current,
                reason: event.reason || 'native_sip_ended',
              });
            }
            activeCallIdRef.current = null;
            backendCallIdRef.current = null;
            backendSessionIdRef.current = null;
            didConnectRef.current = false;
            inFlightRef.current = false;
            setIsSpeakerOn(false);
            setIsOnHold(false);
            setIsMuted(false);
            if (event.reason && event.reason !== 'NORMAL_CLEARING') {
              console.warn('[NativeSip] ended:', event.reason, event.sipCode);
              const copy = describeCallFailure(event.reason, event.sipCode ?? null, connectedAtEnd);
              setFailure(copy);
              setError(callFailureLine(copy));
            } else {
              setFailure(null);
            }
            break;
          }
          case 'registrationFailed': {
            registeredSipIdentityRef.current = null;
            registerPromiseRef.current = null;
            // The native UAC stays registered between calls. A background
            // re-REGISTER can fail after a previous successful call; that must
            // not flip an idle/active call UI to failed or show a false toast.
            // Only the foreground REGISTER while a new user-initiated call is
            // starting is fatal to that attempt.
            if (!inFlightRef.current || activeCallIdRef.current) {
              console.warn('[NativeSip] registrationFailed suppressed (not foreground call setup):', event.reason);
              break;
            }

            // Infrastructure failure (e.g. 403/408 from directory lookup).
            // Mask from the user - diagnostics still carry the raw reason.
            const regCopy = describeCallFailure(event.reason, null, false);
            const masked = callFailureLine(regCopy);
            console.warn('[NativeSip] registrationFailed (masked):', event.reason);
            setFailure(regCopy);
            setError(masked);
            setCallStatus('failed');
            inFlightRef.current = false;
            toast.error(masked);
            break;
          }

          case 'mediaStats':
            // W2: forward native RTP stats to any consumer (call-quality
            // monitor, diagnostics overlay) via the same window-event bridge
            // the web SIP path uses. Keeps Audit-01 observability promise:
            // jitter / loss / RTT visible on every engine.
            try {
              window.dispatchEvent(
                new CustomEvent('elefa:call:mediastats', {
                  detail: {
                    callId: event.callId,
                    rttMs: event.rttMs,
                    lossPct: event.lossPct,
                    jitterMs: event.jitterMs,
                    source: 'native-libwebrtc',
                  },
                }),
              );
            } catch { /* SSR / no window */ }
            break;
        }
      });
      listenerRef.current = handle;
    })();

    return () => {
      cancelled = true;
      cancelledLifecycle = true;
      lifecycleHandle?.remove();
      listenerRef.current?.remove();
      stopDurationTimer();
    };

  }, [startDurationTimer, stopDurationTimer]);

  // W3: react to OS-level connectivity changes. If the device loses the
  // network mid-call we flip a degraded flag immediately so UI shows
  // "Reconnecting…" rather than a stale "connected". When connectivity
  // returns we clear the flag; the native SIP keepalive owns actual
  // re-REGISTER (SipUac.swift / SipUac.kt already schedule that).
  // W4: iOS CallKit reverse-sync. Android native SIP does not create a
  // ConnectionService call slot, so Android ConnectionService "end" events
  // can be stale/presentation-layer echoes and must not tear down ElefaSip.
  useEffect(() => {
    const onOnline = () => {
      if (activeCallIdRef.current) setIsNetworkDegraded(false);
    };
    const onOffline = () => {
      if (activeCallIdRef.current) {
        setIsNetworkDegraded(true);
        console.warn('[NativeSip] network offline during active call');
      }
    };
    const onLockscreenEnd = () => {
      const id = activeCallIdRef.current;
      if (!id) return;
      console.log('[NativeSip] lockscreen End - hanging up SIP dialog');
      if (isNativeSipRuntimeEnabled()) {
        ElefaSipPlugin.hangup({ callId: id }).catch(() => { /* native already terminated */ });
      }
      activeCallIdRef.current = null;
      inFlightRef.current = false;
      setCallStatus('ended');
      stopDurationTimer();
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    if (Capacitor.getPlatform() === 'ios') {
      window.addEventListener('elefa:callkit:end', onLockscreenEnd);
    }
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('elefa:callkit:end', onLockscreenEnd);
    };
  }, [stopDurationTimer]);


  const initiateCall = useCallback(
    async (destinationNumber: string, countryCode: string) => {
      if (!isNativeSipRuntimeEnabled()) {
        throw new Error('Native SIP plugin not available');
      }

      // Re-entry guard. A duplicate initiateCall() while a call is active
      // causes CallKit to reject the second CXStartCallAction (error 7) and
      // the SBC tears down the first leg with an inbound BYE.
      if (inFlightRef.current || activeCallIdRef.current) {
        console.warn(
          '[NativeSip] initiateCall ignored - call already in progress',
          { activeCallId: activeCallIdRef.current, inFlight: inFlightRef.current },
        );
        return activeCallIdRef.current;
      }
      inFlightRef.current = true;
      cancelRequestedRef.current = false;

      // Preemptive teardown: if a previous native callId is still tracked
      // (audio failed mid-call, callee hung up, but the SBC leg may still be
      // alive), send hangup before INVITE. Without this, FreeSWITCH's per-user
      // maxchannels=1 limit rejects the new INVITE with SIP 503 SWITCH_CONGESTION
      // ("Termination is not available"). Safe to call on an already-terminated
      // id — native swallows the error.
      const staleCallId = activeCallIdRef.current || backendCallIdRef.current;
      if (staleCallId) {
        try {
          await ElefaSipPlugin.hangup({ callId: staleCallId });
        } catch {
          /* native already terminated — ignore */
        }
        activeCallIdRef.current = null;
      }

      backendCallIdRef.current = null;
      backendSessionIdRef.current = null;
      didConnectRef.current = false;
      endNotifiedRef.current = false;

      setError(null);
      setFailure(null);
      setDuration(0);
      setCallStatus('starting');

      // 1. Ask edge function for SIP creds + WSS endpoint + accountcode
      const { data, error: invokeError } = await supabase.functions.invoke<ConnectStartResponse>(
        'freeswitch-connect-start',
        { body: { destinationNumber, countryCode } },
      );

      if (invokeError || !data?.success) {
        const failure = await parseConnectStartFailure(data, invokeError);
        const msg = failure.message || failure.error || 'connect-start failed';
        const startCopy = describeCallFailure(msg, null, false);
        setFailure(startCopy);
        setError(callFailureLine(startCopy));
        setCallStatus('failed');
        throw new Error(msg);
      }

      // Pre-ring cancel check #1: edge function returned, but user already
      // tapped End. Don't proceed to SIP register/INVITE.
      if (cancelRequestedRef.current) {
        cancelRequestedRef.current = false;
        inFlightRef.current = false;
        setCallStatus('ended');
        return null;
      }

      // Edge-function callId is the CDR correlation key (X-Call-Id header);
      // the native plugin generates its own CallKit UUID which becomes the
      // authoritative SIP Call-ID and the callId emitted in sipEvents.
      const cdrCallId = data.callId;
      backendCallIdRef.current = data.callId;
      backendSessionIdRef.current = data.sessionId;
      setCallData({
        callId: data.callId,
        sessionId: data.sessionId,
        destination: data.destination,
        ratePerMinuteCents: data.ratePerMinuteCents || 0,
        reservedCents: data.reservedCents || 0,
        callerId: data.callerId,
        astppAccountNumber: data.astppAccountNumber,
        availableBalanceCents: data.availableBalanceCents,
        minimumRequiredCents: data.minimumRequiredCents,
        maxCallSeconds: data.maxCallSeconds ?? data.maxSeconds ?? null,
        lowBalanceWarningSeconds: data.lowBalanceWarningSeconds ?? null,
      });
      setCallStatus('connecting');

      // 2. REGISTER + INVITE through native engine
      try {
        const iceServers = await loadIceServers();
        const sipIdentity = `${data.sipServer}|${data.sipServerTls}|${data.sipUsername}|${data.sipPassword}`;
        // Always ask the native engine to ensure registration before INVITE.
        // The native UAC is idempotent for the same per-user identity, so this
        // does not link accounts or rotate credentials; it only repairs stale
        // JS/native registration state after app backgrounding or backend redeploys.
        if (!registerPromiseRef.current) {
          registerPromiseRef.current = ElefaSipPlugin.register({
            sipServerTls: data.sipServerTls,
            sipServerWss: data.sipServer,
            username: data.sipUsername,
            password: data.sipPassword,
            displayName: data.callerId,
          })
            .then(() => {
              registeredSipIdentityRef.current = sipIdentity;
            })
            .catch((registerError) => {
              registeredSipIdentityRef.current = null;
              throw registerError;
            })
            .finally(() => {
              registerPromiseRef.current = null;
            });
        }
        await registerPromiseRef.current;

        // Pre-ring cancel check #2: registered but user tapped End.
        if (cancelRequestedRef.current) {
          cancelRequestedRef.current = false;
          inFlightRef.current = false;
          setCallStatus('ended');
          return null;
        }

        const result = await ElefaSipPlugin.call({
          destination: data.dialDestination,
          callerId: data.callerId,
          displayName: data.callerId,
          accountcode: data.astppAccountNumber,
          iceServers,
          // Header set is kept 1:1 with the working web INVITE
          // (src/hooks/useFreeSwitchCall.ts:715-723) so the FS dialplan in
          // 001_set_accountcode.xml sees identical inputs from both clients.
          customHeaders: {
            'X-User-Id': (await supabase.auth.getUser()).data.user?.id ?? '',
            'X-Call-Id': cdrCallId,
            'X-Caller-Id': data.callerId,
            'X-Display-Ani': data.callerId,
            'X-Max-Call-Seconds': String(data.maxCallSeconds ?? data.maxSeconds ?? ''),
            ...(data.callerId
              ? { 'P-Asserted-Identity': `<sip:${data.callerId}@fs.elefa.tel>` }
              : {}),
            ...(data.astppAccountNumber
              ? { 'X-Account-Code': data.astppAccountNumber }
              : {}),
          },
        });

        // Plugin owns the authoritative callId from here on.
        const nativeCallId = result.callId || cdrCallId;
        setCallId(nativeCallId);
        activeCallIdRef.current = nativeCallId;

        // Pre-ring cancel check #3: INVITE went out but the user tapped End
        // while we were awaiting it. Hang up the now-known native call id
        // immediately so the SBC tears the leg down before the callee rings.
        if (cancelRequestedRef.current) {
          cancelRequestedRef.current = false;
          try {
            await ElefaSipPlugin.hangup({ callId: nativeCallId });
          } catch (e) {
            console.warn('[NativeSip] post-INVITE cancel hangup error', e);
          }
          activeCallIdRef.current = null;
          inFlightRef.current = false;
          setCallStatus('ended');
          return null;
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'native call failed';
        if (/not registered|register/i.test(msg)) registeredSipIdentityRef.current = null;
        const inviteCopy = describeCallFailure(msg, null, false);
        setFailure(inviteCopy);
        setError(callFailureLine(inviteCopy));
        setCallStatus('failed');
        inFlightRef.current = false;
        activeCallIdRef.current = null;
        throw e;
      }

      return activeCallIdRef.current ?? cdrCallId;
    },
    [],
  );

  const endCall = useCallback(async () => {
    // Mark cancellation SYNCHRONOUSLY in the user's tap gesture so any
    // in-flight initiateCall() await can short-circuit at its next checkpoint.
    cancelRequestedRef.current = true;

    setCallStatus('ending');
    // If the native plugin already owns a callId, hang up immediately.
    // Otherwise fall back to the CDR/backend callId so the edge layer can
    // correlate the cancellation, then trust the cancel checkpoints in
    // initiateCall to bail before the INVITE actually fires.
    const id = activeCallIdRef.current ?? backendCallIdRef.current;
    if (id && isNativeSipRuntimeEnabled()) {
      try {
        await ElefaSipPlugin.hangup({ callId: id });
      } catch (e) {
        console.warn('[NativeSip] hangup error', e);
      }
    }
    // Frontend hangup resilience: 1.5s grace before flipping to ended
    setTimeout(() => setCallStatus('ended'), 1500);
    stopDurationTimer();
    activeCallIdRef.current = null;
    inFlightRef.current = false;
    setIsSpeakerOn(false);
    setIsOnHold(false);
    setIsMuted(false);
  }, [stopDurationTimer]);

  const toggleMute = useCallback(async () => {
    if (!isNativeSipRuntimeEnabled()) return;
    const next = !isMuted;
    await ElefaSipPlugin.setMuted({ muted: next });
    setIsMuted(next);
  }, [isMuted]);

  const toggleSpeaker = useCallback(async () => {
    if (!isNativeSipRuntimeEnabled()) return;
    const next = !isSpeakerOn;
    await ElefaSipPlugin.setSpeaker({ on: next });
    setIsSpeakerOn(next);
  }, [isSpeakerOn]);

  // R1: native hold. ElefaSip plugin owns CallKit/Telecom internally and
  // mirrors the held state to the lockscreen pill, so we only need to drive
  // setHold here and reflect the result in React state. Previously the
  // useActiveCallProvider native branch was a no-op which caused the iOS
  // lockscreen to lie about call state (audio paused, UI still "active").
  const toggleHold = useCallback(async () => {
    if (!isNativeSipRuntimeEnabled() || !activeCallIdRef.current) return;
    const next = !isOnHold;
    try {
      await ElefaSipPlugin.setHold({ hold: next });
      setIsOnHold(next);
    } catch (e) {
      console.warn('[NativeSip] setHold failed', e);
    }
  }, [isOnHold]);

  const sendDTMF = useCallback(async (digits: string) => {
    if (!isNativeSipRuntimeEnabled()) return;
    await ElefaSipPlugin.sendDTMF({ digits });
  }, []);

  const reset = useCallback(() => {
    setCallStatus('idle');
    setCallId(null);
    setCallData(null);
    setError(null);
    setFailure(null);
    setDuration(0);
    setIsMuted(false);
    setIsOnHold(false);
    setIsSpeakerOn(false);
    setIsNetworkDegraded(false);
    activeCallIdRef.current = null;
    inFlightRef.current = false;
    stopDurationTimer();
  }, [stopDurationTimer]);

  return {
    callStatus,
    callId,
    callData,
    error,
    failure,
    clearFailure: () => setFailure(null),
    duration,
    isMuted,
    isOnHold,
    isSpeakerOn,
    isNetworkDegraded,
    isCallActive: callStatus === 'connected' || callStatus === 'ringing' || callStatus === 'connecting',
    initiateCall,
    endCall,
    toggleMute,
    toggleSpeaker,
    toggleHold,
    sendDTMF,
    reset,
  };
}

